import { parseCarouselName } from '@/hooks/useTypesenseCarousels/parse-carousel-name';
import type {
  DocumentSchema,
  SearchResponse,
  SearchResponseHit,
  TypesenseCarousel,
} from '@music-vine/setlist';
import type { TypesenseCarouselProcessedResult } from './types';
import type { DebugInfoReadoutItem } from '@/components/DebugInfoReadout';
import { AUTO_CAROUSEL_ADD_UP_TO_THRESHOLD } from '@music-vine/setlist';

export const parseCarousels = (
  searchResponse: SearchResponse<DocumentSchema>
): TypesenseCarousel[] => {
  let carousels: TypesenseCarousel[] =
    searchResponse?.hits?.map((hit) => hit.document as TypesenseCarousel) || [];

  // Remove identifier info from name
  carousels = carousels.map((carousel) => ({
    ...carousel,
    name: parseCarouselName(carousel.name),
    tiles: carousel.tiles
      .filter((tile) => !!tile.slug)
      .map((tile) => ({
        ...tile,
        title: parseCarouselName(tile.title),
      })),
  }));

  return carousels;
};

interface SearchResponseVectorHit extends SearchResponseHit<DocumentSchema> {
  vector_distance?: number;
}

interface SearchResponseVector extends SearchResponse<DocumentSchema> {
  hits?: SearchResponseVectorHit[];
}

const parseAutomaticCarousels = (
  searchResponse: SearchResponseVector | undefined
): TypesenseCarousel[] => {
  const searchResponseTiles =
    searchResponse?.hits?.map((hit, index) => ({
      title: hit.document.name,
      description: hit.document.description,
      debug_info: hit.vector_distance
        ? {
            vector_distance: hit.vector_distance,
            keywords: hit.document.keywords,
          }
        : { matchType: 'Keyword', keywords: hit.document.keywords },
      image: hit.document.image,
      order: index + 1,
      slug: hit.document.relative_url,
    })) || [];

  // Remove identifier info from name
  const carousels = [
    {
      id: 'automatic-1',
      order: 1,
      name: 'Automatic',
      page_slug: '',
      show_carousel_description: false,
      show_carousel_title: true,
      tile_display_type: 1,
      tiles: searchResponseTiles,
    },
  ];

  return carousels;
};

const getTotalTiles = (carousels: TypesenseCarousel[]) => {
  let totalTiles = 0;

  for (const carousel of carousels) {
    totalTiles += carousel.tiles.length;
  }
  return totalTiles;
};

const getHighestOrderValue = (carousels: TypesenseCarousel[]) => {
  let highestOrderValue = 0;

  for (const carousel of carousels) {
    if (carousel.tiles.length === 0) {
      continue;
    }
    const carouselHighestOrderValue = Math.max(
      ...carousel.tiles.map((o) => o.order)
    );
    if (carouselHighestOrderValue > highestOrderValue) {
      highestOrderValue = carouselHighestOrderValue;
    }
  }
  return highestOrderValue;
};

export const parseCarouselsCombined = (
  searchResponse: SearchResponse<DocumentSchema>[]
): TypesenseCarouselProcessedResult => {
  const pageCarousels = parseCarousels(searchResponse[0]);
  const automaticCarousel = parseAutomaticCarousels(searchResponse[1]);
  const hasPageCarousels = pageCarousels.length !== 0;
  const useAutoFallback = !hasPageCarousels;
  const combinedCarousels = structuredClone(
    useAutoFallback ? automaticCarousel : pageCarousels
  );
  const firstCarouselTiles = combinedCarousels[0]?.tiles ?? [];
  const totalTiles = getTotalTiles(combinedCarousels);
  const highestOrderValue = getHighestOrderValue(combinedCarousels);

  const debugInfo: DebugInfoReadoutItem[] = [
    {
      title: 'Has Retool carousel',
      value: hasPageCarousels ? `Yes (${pageCarousels.length})` : 'No',
    },
  ];

  if (
    hasPageCarousels &&
    automaticCarousel[0].tiles.length > 0 &&
    totalTiles < AUTO_CAROUSEL_ADD_UP_TO_THRESHOLD
  ) {
    for (const autoTile of automaticCarousel[0].tiles) {
      if (
        getTotalTiles(combinedCarousels) < AUTO_CAROUSEL_ADD_UP_TO_THRESHOLD &&
        !firstCarouselTiles.some((el) => el.slug === autoTile.slug)
      ) {
        autoTile.order += highestOrderValue;
        firstCarouselTiles.push(autoTile);
      }
    }

    // Turn off descriptions for all carousels if automatic tiles have been used,
    // as extra short descriptions aren't in this collection yet.
    // TODO: Remove this when extra short descriptions are added to Typesense
    for (const carousel of combinedCarousels) {
      carousel.show_carousel_description = false;
    }
  }

  debugInfo.push({
    title: 'Total tiles',
    value: getTotalTiles(combinedCarousels),
  });

  return {
    carousels: combinedCarousels,
    debug_info: debugInfo,
  };
};
